import { Box, Typography } from "@mui/material";
import { useEffect, useState } from "react";

import { Spacing } from "Infrastructure/Themes/Themes";
import {
  getTranslation,
  Resources,
  useResource,
} from "Infrastructure/Translations/Resources";
import { BlModal } from "Shared/Dialogs/BlModal";
import { BlInput } from "Shared/Inputs/BlInput";

interface AddVariableDialogProps {
  open: boolean;
  /** Pre-filled name suggestion (e.g. from selected text) */
  suggestedName?: string;
  /** Pre-filled default value (from selected text) */
  selectedText?: string;
  onConfirm: (name: string, defaultValue: string) => void;
  onClose: () => void;
}

export const AddVariableDialog = ({
  open,
  suggestedName = getTranslation(Resources.JobDetail.DefaultVariableName),
  selectedText = "",
  onConfirm,
  onClose,
}: AddVariableDialogProps): React.ReactNode => {
  const { t } = useResource();
  const [name, setName] = useState(suggestedName);
  const [defaultValue, setDefaultValue] = useState(selectedText);

  useEffect(() => {
    if (open) {
      setName(suggestedName);
      setDefaultValue(selectedText);
    }
  }, [open, suggestedName, selectedText]);

  const sanitisedName = name.trim().replace(/\s+/g, "_");

  const handleConfirm = () => {
    if (!sanitisedName) return;
    onConfirm(sanitisedName, defaultValue);
    onClose();
  };

  return (
    <BlModal
      isOpen={open}
      title={t(Resources.JobDetail.AddVariableDialogTitle)}
      onCancel={onClose}
      onConfirm={handleConfirm}
      cancelButtonText={t(Resources.Common.Cancel)}
      confirmButtonText={t(Resources.JobDetail.AddVariableDialogConfirm)}
      maxWidth="450px"
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: Spacing.M }}>
        <Box>
          <BlInput
            labelProps={{ label: t(Resources.JobDetail.VariableName) }}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 0.5,
              color: (theme) => theme.palette.text.secondary,
              fontFamily: "monospace",
            }}
          >
            {`{{${sanitisedName || "name"}}}`}
          </Typography>
        </Box>
        <BlInput
          labelProps={{ label: t(Resources.JobDetail.VariableDefaultValue) }}
          value={defaultValue}
          onChange={(e) => setDefaultValue(e.target.value)}
        />
      </Box>
    </BlModal>
  );
};
